import { BillingInvoice, BillingInvoiceStatus } from '@apis/Invoices/model';
import { Box, Button, Drawer, LoadingOverlay, Text, Tooltip, UnstyledButton, useMantineTheme } from '@mantine/core';
import { useDi, useDiComponent } from '@root/Services/DI';
import { FormatService } from '@root/Services/FormatService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataColumnConfig } from '../DataGrid/Models';
import { DataGrid } from '../DataGrid';
import {
    getBillingInvoiceGetAllBillingInvoices,
    getBillingInvoiceGetBillingInvoices,
    postBillingInvoiceGetAdminBillingInvoiceStatuses,
    postBillingInvoiceGetBillingInvoiceStatuses,
} from '@apis/Invoices';
import { CompanyContext } from '../Router/CompanyContext';
import { PagePanel } from '@root/Design/Layout';
import { BillingInvoiceSidePanel } from '@root/Site/Billing/BillingInvoicesSidePanel';
import { postCompanyStatsQueryCompanyStats } from '@apis/Customers';
import { queryBuilder } from '@root/Services/QueryExpr';
import { CompanyStat } from '@apis/Customers/model';
import { BasicApi } from '@root/Services/BasicApi';
import { ArrowRight, Download } from 'tabler-icons-react';
import { AnchorButton } from '@root/Design/Primitives';
import { useNav } from '@root/Services/NavigationService';
import { useLink } from '@root/Services/Router/Router';
import { Route } from '@root/Services/Router/RouteSerializer';
import { FillerSwitch } from '@root/Design/Filler';
import { useWindowSize } from '@react-hookz/web';
import { useMainNav } from '@root/Design/Nav';

interface BillingInvoiceWithCompany {
    billingInvoice: BillingInvoice;
    companyName: string;
    billingStatus?: BillingInvoiceStatus;
}

export const BillingInvoicesGrid = (props: { companyId?: number; isAdminView?: boolean }) => {
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
    const [invoicesWithCompany, setInvoicesWithCompany] = useState<BillingInvoiceWithCompany[]>([]);
    const [selectedInvoice, setSelectedInvoice] = useState<BillingInvoiceWithCompany | null>(null);
    const [openBillingSidePanel, setOpenBillingSidePanel] = useState(false);
    const [previewCollapsed, setPreviewCollapsed] = useState(true);
    const [title, setTitle] = useState<string>('');
    const [subTitle, setSubTitle] = useState<string>('');
    const [shouldReload, setShouldReload] = useState(false);
    const shouldReloadRef = useRef(false);
    const fmtSvc = useDi(FormatService);
    const theme = useMantineTheme();

    useEffect(() => {
        loadBillingInvoices();
    }, []);

    useEffect(() => {
        if (selectedInvoice !== null) {
            props.companyId ? setOpenBillingSidePanel(true) : setOpenBillingSidePanel(true);
        }
    }, [selectedInvoice]);

    const loadBillingInvoices = async () => {
        setLoading(true);
        if (props.companyId) {
            await getBillingInvoiceGetBillingInvoices().then((response) => {
                setInvoices(response);
            });
        } else {
            await getBillingInvoiceGetAllBillingInvoices().then((response) => {
                setInvoices(response);
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        (async () => {
            if (invoices.length > 0) {
                let companyInvoices = invoices;
                if (props.companyId) {
                    companyInvoices = invoices.filter((invoice) => invoice.CompanyId === props.companyId);
                }
                if (companyInvoices.length === 0) {
                    return;
                }
                const companyIds = companyInvoices.map((invoice) => invoice.CompanyId);

                const companyStats = (
                    await queryBuilder<CompanyStat>()
                        .where((b) => b.model.Id!.eq(companyIds as number[]))
                        .select((b) => ({
                            CompanyName: b.model.CompanyName,
                            Id: b.model.Id,
                            Ct: b.count(),
                        }))
                        .execute(postCompanyStatsQueryCompanyStats)
                ).Results;

                const invoiceIds = companyInvoices.map((i) => i.Id!);
                const statusMap = props.isAdminView
                    ? await postBillingInvoiceGetAdminBillingInvoiceStatuses(invoiceIds)
                    : await postBillingInvoiceGetBillingInvoiceStatuses(invoiceIds);

                const invoicesWithCompany: BillingInvoiceWithCompany[] = companyInvoices.map((invoice) => {
                    const status = statusMap[invoice.Id!];
                    return {
                        billingInvoice: invoice,
                        companyName: companyStats?.find((c) => c.Id === invoice.CompanyId)?.CompanyName ?? '',
                        billingStatus: status,
                    };
                });
                setInvoicesWithCompany(invoicesWithCompany);
            }
        })();
    }, [invoices]);

    useEffect(() => {
        if (props.companyId && selectedInvoice) {
            setSubTitle('Invoice Details');
            setTitle(fmtSvc.formatShortMonthYear(new Date(selectedInvoice?.billingInvoice.Year!, selectedInvoice?.billingInvoice.Month! - 1)));
        }
    }, [selectedInvoice]);
    const link = useLink();

    let columns = useMemo(() => {
        type Options = Array<{ label: string; value: string }>;
        const { terms } = invoices.reduce(
            (res, item) => {
                if (item.BillingTerm && !res.terms.find((term) => term.value === item.BillingTerm)) {
                    res.terms.push({ label: item.BillingTerm, value: item.BillingTerm });
                }
                return res;
            },
            { terms: [] as Options }
        );
        const baseColumns = [
            {
                id: 'BillingPeriod',
                header: 'Billing Period',
                accessor: (item) => new Date(item.billingInvoice.Year!, item.billingInvoice.Month! - 1),
                sortField: 'BillingPeriod',
                defaultWidth: 175,
                type: 'date',
                formatter: (_, value) => {
                    return fmtSvc.formatShortMonthYear(value);
                },
                exportOptions: {
                    renderer: (item) => {
                        return fmtSvc.formatShortMonthYear(new Date(item.billingInvoice.Year!, item.billingInvoice.Month! - 1));
                    },
                },
            },
            {
                id: 'BillingTerm',
                accessor: (item) => item.billingInvoice.BillingTerm,
                header: 'Billing Term',
                align: 'left',
                type: 'string',
                defaultWidth: 150,
                sortField: 'BillingTerm',
                filter: {
                    filterType: 'string',
                    name: 'Billing Term',
                    filterField: 'BillingTerm',
                    options: {
                        getValueProvider: () => {
                            return terms;
                        },
                    },
                },
            },
            {
                id: 'Total',
                header: 'Total',
                accessor: (item) => item.billingInvoice.Total,
                defaultWidth: 100,
                sortField: 'Total',
                type: 'number',
                align: 'right',
                filter: {
                    filterType: 'number',
                    filterField: 'Total',
                    name: 'Total',
                },
                formatter: (row) => {
                    return row.billingInvoice.Total ? fmtSvc.formatMoney(row.billingInvoice.Total) : '';
                },
            },
            {
                id: 'Status',
                header: 'Status',
                accessor: (item) => item.billingInvoice.Status,
                defaultWidth: 100,
                sortField: 'Status',
                type: 'string',
                filter: {
                    filterType: 'string',
                    name: 'Status',
                    filterField: 'Status',
                    options: {
                        getValueProvider: () => {
                            return props.isAdminView
                                ? ['Finalized', 'Voided', 'Paid', 'Draft', 'Sent'].map((v) => ({ label: v, value: v }))
                                : [
                                      { label: 'Finalized', value: 'Issued' },
                                      { label: 'Sent', value: 'Sent' },
                                      { label: 'Paid', value: 'Paid' },
                                      { label: 'Voided', value: 'Voided' },
                                      { label: 'Draft', value: 'Pending' },
                                  ];
                        },
                    },
                },
                cellRenderer: (row) => {
                    var customerFriendlyName =
                        row.billingInvoice.Status === 'Finalized'
                            ? 'Issued'
                            : row.billingInvoice.Status === 'Draft'
                            ? 'Pending'
                            : row.billingInvoice.Status;
                    return props.isAdminView ? row.billingInvoice.Status : customerFriendlyName;
                },
            },
            {
                id: 'ViewInvoiceExplorer',
                header: 'View In Invoice Explorer',
                accessor: (item) => item.billingInvoice.Id,
                defaultWidth: 225,
                sortField: 'Id',
                type: 'string',
                align: 'center',
                cellRenderer: (item) => {
                    const { getRootUrl, getDescendUrl } = useNav();
                    const month = `${item.billingInvoice.Year}${String(item.billingInvoice.Month).padStart(2, '0')}01`;
                    const id = item.billingInvoice.CompanyId ?? 0;
                    const route = [
                        { name: 'manage-company', data: { id } },
                        { name: 'FinOps', data: {} },
                        { name: 'invoice-explorer', data: { month, view: 'csinvoice' } },
                    ] as Route;
                    const url = props.isAdminView ? getRootUrl(route) : getDescendUrl('invoice-explorer', { month: month, view: 'csinvoice' });
                    return <AnchorButton {...link(url)} text="View in Invoice Explorer" icon={<ArrowRight size={16} />} />;
                },
                exportOptions: {
                    hidden: true,
                },
            },
            {
                id: 'Download',
                header: 'Download',
                accessor: (item) => item.billingInvoice.Id,
                defaultWidth: 150,
                sortField: 'Id',
                type: 'string',
                align: 'center',
                cellRenderer: (item) => {
                    return item.billingInvoice.FileKey ? (
                        <UnstyledButton sx={{ color: theme.colors.primary[7], fontSize: '14px' }} onClick={() => downloadFile(item.billingInvoice)}>
                            <Download size={16} /> Download
                        </UnstyledButton>
                    ) : (
                        'No File Available'
                    );
                },
                exportOptions: {
                    hidden: true,
                },
            },
            {
                id: 'CreatedAt',
                header: 'Created At',
                accessor: (item) => item.billingInvoice.CreatedAt,
                defaultWidth: 100,
                sortField: 'CreatedAt',
                type: 'date',
                filter: {
                    filterType: 'date',
                    name: 'Created At',
                    filterField: 'CreatedAt',
                },
                formatter: (row) => {
                    return row.billingInvoice.CreatedAt ? fmtSvc.formatDate(fmtSvc.toLocalDate(row.billingInvoice.CreatedAt)) : '';
                },
                exportOptions: {
                    renderer: (row) => {
                        return row.billingInvoice.CreatedAt ? fmtSvc.formatDate(fmtSvc.parseDateNoTime(row.billingInvoice?.CreatedAt)) : '';
                    },
                },
            },
            {
                id: 'ModifiedAt',
                header: 'Last Updated',
                accessor: (item) => item.billingStatus?.ModifiedAt,
                defaultWidth: 150,
                sortField: 'ModifiedAt',
                type: 'date',
                filter: {
                    filterType: 'date',
                    name: 'Last Updated',
                    filterField: 'ModifiedAt',
                },
                cellRenderer: (row) => {
                    return row.billingStatus?.ModifiedAt ? fmtSvc.formatDate(fmtSvc.parseDateNoTime(row.billingStatus?.ModifiedAt)) : '';
                },
            },
        ] as DataColumnConfig<BillingInvoiceWithCompany>[];
        if (!props.companyId) {
            baseColumns.unshift({
                id: 'companyName',
                header: 'Company Name',
                accessor: 'companyName',
                defaultWidth: 250,
                sortField: 'companyName',
                type: 'string',
            });
        }

        return baseColumns;
    }, [invoices]);

    const onUnloadCompany = useCallback(() => {
        setSelectedInvoice(null);
        if (shouldReloadRef.current) {
            shouldReloadRef.current = false;
            setShouldReload(false);
            loadBillingInvoices();
        }
    }, [setSelectedInvoice]);

    const handleCloseSidePanel = (didEdit: boolean) => {
        setOpenBillingSidePanel(false);
        setPreviewCollapsed(true);
        shouldReloadRef.current = didEdit;
        setShouldReload(didEdit);
        if (didEdit && props.companyId) {
            loadBillingInvoices();
        }
    };
    const basicApi = useDi(BasicApi);
    const downloadFile = (invoice: BillingInvoice) => {
        const params = {
            invoiceId: invoice.Id,
        };

        const fileName = `Cloud Invoice - ${invoice.Year}-${invoice.Month!.toString().padStart(2, '0')}`;
        basicApi.download(`${fileName}.pdf`, { url: '/BillingInvoice/DownloadInvoiceFile', method: 'GET', params }, 'Invoices');
    };

    const { width: windowW } = useWindowSize();
    const { width: mainNavW } = useMainNav();
    const drawerWidth = windowW - mainNavW;

    return (
        <>
            {openBillingSidePanel && (
                <Drawer
                    opened={openBillingSidePanel}
                    onClose={() => handleCloseSidePanel(false)}
                    size={previewCollapsed ? 700 : drawerWidth}
                    padding={0}
                    position="right"
                    overlayBlur={previewCollapsed ? 0 : 4}
                    overlayOpacity={0.1}
                    withinPortal={false}
                    withCloseButton={false}
                    shadow="md"
                >
                    {!props.companyId ? (
                        <CompanyContext
                            unloadOnUnmount
                            companyId={selectedInvoice?.billingInvoice.CompanyId!}
                            type="company"
                            onUnload={onUnloadCompany}
                        >
                            {() => (
                                <PagePanel padded={false} size="md" style={{ minWidth: '350px', width: '100%', height: '100%' }}>
                                    <BillingInvoiceSidePanel
                                        toggleSidePanel={setOpenBillingSidePanel}
                                        fmtSvc={fmtSvc}
                                        onClose={handleCloseSidePanel}
                                        billingInvoice={selectedInvoice?.billingInvoice!}
                                        onPreviewCollapsedChange={setPreviewCollapsed}
                                        settingsWidth={previewCollapsed ? 700 : 500}
                                    ></BillingInvoiceSidePanel>
                                </PagePanel>
                            )}
                        </CompanyContext>
                    ) : (
                        <PagePanel padded={false} size="md" style={{ minWidth: '350px', width: '100%', height: '100%' }}>
                            <BillingInvoiceSidePanel
                                toggleSidePanel={setOpenBillingSidePanel}
                                fmtSvc={fmtSvc}
                                onClose={handleCloseSidePanel}
                                billingInvoice={selectedInvoice?.billingInvoice!}
                                title={title}
                                subtitle={subTitle}
                                onPreviewCollapsedChange={setPreviewCollapsed}
                                settingsWidth={previewCollapsed ? 700 : 500}
                            ></BillingInvoiceSidePanel>
                        </PagePanel>
                    )}
                </Drawer>
            )}
            <FillerSwitch loading={loading} noData={invoicesWithCompany.length === 0} noDataMessage="No Invoices Found">
                {() => (
                    <DataGrid
                        columns={columns}
                        dataSource={invoicesWithCompany!}
                        onRowClick={(row) => {
                            if (props.isAdminView) {
                                setSelectedInvoice(row);
                            }
                        }}
                        allowMultiSort
                        statePersistence={{ key: 'BillingInvoices' }}
                        allowSavedViews
                        hideGlobalSearch={true}
                        hideColumnSelector={true}
                    ></DataGrid>
                )}
            </FillerSwitch>
        </>
    );
};
